import React, { useState, useCallback } from 'react';
import { Plus, X, Edit2, Trash2, Eye, EyeOff, Copy, Check, UserPlus } from 'lucide-react';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const DEFAULT_AVAILABILITY = {
  monday: { available: true, start: '08:00', end: '17:00' },
  tuesday: { available: true, start: '08:00', end: '17:00' },
  wednesday: { available: true, start: '08:00', end: '17:00' },
  thursday: { available: true, start: '08:00', end: '17:00' },
  friday: { available: true, start: '08:00', end: '17:00' },
  saturday: { available: false, start: '08:00', end: '17:00' },
  sunday: { available: false, start: '08:00', end: '17:00' }
};

// Generate a random temporary password
function generateTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '!@#$%';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  // Add a special char and digit to ensure strength
  password += specials.charAt(Math.floor(Math.random() * specials.length));
  password += Math.floor(Math.random() * 10);
  return password;
}

function EmployeeForm({ employee, onSave, onCancel }) {
  const isEditing = !!employee;

  const [formData, setFormData] = useState({
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    role: employee?.role || 'employee',
    hourlyRate: employee?.hourlyRate || '',
    payRates: employee?.payRates || [],
    status: employee?.status || 'active',
    hireDate: employee?.hireDate || new Date().toISOString().split('T')[0],
    availability: employee?.availability || DEFAULT_AVAILABILITY,
    defaultShift: employee?.defaultShift || { startTime: '07:30', endTime: '16:00' }
  });

  const [errors, setErrors] = useState({});
  const [newPayRate, setNewPayRate] = useState({ label: '', rate: '' });
  const [editingPayRateIdx, setEditingPayRateIdx] = useState(null);
  const [editPayRate, setEditPayRate] = useState({ label: '', rate: '' });

  // Login account creation state (only for new employees)
  const [createLogin, setCreateLogin] = useState(!isEditing);
  const [tempPassword, setTempPassword] = useState(() => generateTempPassword());
  const [showPassword, setShowPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const handleCopyPassword = useCallback(() => {
    navigator.clipboard.writeText(tempPassword).then(() => {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    });
  }, [tempPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: field === 'available' ? value : value
        }
      }
    }));
  };

  const handleDefaultShiftChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      defaultShift: {
        ...prev.defaultShift,
        [field]: value
      }
    }));
  };

  const handleAddPayRate = () => {
    if (!newPayRate.label.trim() || !newPayRate.rate) return;
    setFormData(prev => ({
      ...prev,
      payRates: [...prev.payRates, { label: newPayRate.label.trim(), rate: parseFloat(newPayRate.rate) }]
    }));
    setNewPayRate({ label: '', rate: '' });
  };

  const handleEditPayRate = (idx) => {
    setEditingPayRateIdx(idx);
    setEditPayRate({ ...formData.payRates[idx] });
  };

  const handleSavePayRateEdit = () => {
    if (!editPayRate.label.trim() || !editPayRate.rate) return;
    setFormData(prev => ({
      ...prev,
      payRates: prev.payRates.map((pr, i) =>
        i === editingPayRateIdx ? { label: editPayRate.label.trim(), rate: parseFloat(editPayRate.rate) } : pr
      )
    }));
    setEditingPayRateIdx(null);
    setEditPayRate({ label: '', rate: '' });
  };

  const handleDeletePayRate = (idx) => {
    setFormData(prev => ({
      ...prev,
      payRates: prev.payRates.filter((_, i) => i !== idx)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.hourlyRate && formData.payRates.length === 0) {
      newErrors.hourlyRate = 'Default hourly rate is required (or add pay rates below)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const hourlyRate = formData.hourlyRate
        ? parseFloat(formData.hourlyRate)
        : formData.payRates.length > 0
          ? formData.payRates[0].rate
          : 15;
      const saveData = {
        ...formData,
        hourlyRate,
        payRates: formData.payRates,
        avatar: `${formData.firstName[0]}${formData.lastName ? formData.lastName[0] : ''}`.toUpperCase()
      };

      // Include login creation info for new employees
      if (!isEditing && createLogin) {
        saveData._createLogin = true;
        saveData._tempPassword = tempPassword;
      }

      onSave(saveData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            First Name <span className="required">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            className="form-input"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter first name"
          />
          {errors.firstName && <span className="form-error">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            className="form-input"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
          />
          {errors.lastName && <span className="form-error">{errors.lastName}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Phone <span className="required">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            className="form-input"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(434) 555-0000"
          />
          {errors.phone && <span className="form-error">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            name="email"
            className="form-input"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@twosisterscleaning.com"
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>

        {/* Login Account Creation - only for new employees */}
        {!isEditing && (
          <>
            <div className="form-section-title" style={{ gridColumn: '1 / -1' }}>Login Account</div>
            <div style={{ gridColumn: '1 / -1', marginBottom: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={createLogin}
                  onChange={(e) => setCreateLogin(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#1a2634' }}
                />
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '14px' }}>
                  <UserPlus size={16} />
                  Create login account for this employee
                </span>
              </label>
              <p style={{ margin: '6px 0 0 28px', fontSize: '12px', color: '#6b7280' }}>
                Creates a login so the employee can access the Team App. You'll give them the temporary password below.
              </p>
            </div>

            {createLogin && (
              <div style={{
                gridColumn: '1 / -1',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, fontSize: '13px', color: '#15803d' }}>Temporary Password</span>
                  <button
                    type="button"
                    onClick={() => setTempPassword(generateTempPassword())}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '12px', color: '#15803d', textDecoration: 'underline'
                    }}
                  >
                    Generate new
                  </button>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'white', borderRadius: '8px', padding: '10px 12px',
                  border: '1px solid #d1d5db', fontFamily: 'monospace', fontSize: '16px',
                  letterSpacing: '0.5px'
                }}>
                  <span style={{ flex: 1 }}>
                    {showPassword ? tempPassword : '••••••••••'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#6b7280' }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyPassword}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: copiedPassword ? '#15803d' : '#6b7280' }}
                    title="Copy password"
                  >
                    {copiedPassword ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Share this password with the employee. They'll use their email and this password to log in.
                  They should change it after their first login.
                </p>
              </div>
            )}
          </>
        )}

        <div className="form-section-title">Employment Details</div>

        <div className="form-group">
          <label className="form-label">Role</label>
          <select
            name="role"
            className="form-select"
            value={formData.role}
            onChange={handleChange}
          >
            <option value="employee">Cleaner</option>
            <option value="lead">Team Lead</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            name="status"
            className="form-select"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="onboarding">Onboarding</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            Hourly Rate <span className="required">*</span>
          </label>
          <input
            type="number"
            name="hourlyRate"
            className="form-input"
            value={formData.hourlyRate}
            onChange={handleChange}
            placeholder="18.00"
            step="0.50"
            min="0"
          />
          {errors.hourlyRate && <span className="form-error">{errors.hourlyRate}</span>}
        </div>

        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">Pay Rates</label>
          <p className="form-help-text" style={{ margin: '-4px 0 8px 0', fontSize: '12px', color: '#6b7280' }}>
            Add multiple pay rates if this employee works different roles (e.g., Cleaner $17/hr, Driver $17.50/hr). When they clock in, they'll select which role they're working.
          </p>

          {formData.payRates.length > 0 && (
            <div className="pay-rates-list" style={{ marginBottom: '12px' }}>
              {formData.payRates.map((pr, idx) => (
                <div key={idx} className="pay-rate-item" style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                  background: '#f8fafc', borderRadius: '8px', marginBottom: '6px', border: '1px solid #e2e8f0'
                }}>
                  {editingPayRateIdx === idx ? (
                    <>
                      <input
                        type="text"
                        value={editPayRate.label}
                        onChange={e => setEditPayRate(prev => ({ ...prev, label: e.target.value }))}
                        className="form-input"
                        style={{ flex: 1, padding: '4px 8px', fontSize: '13px' }}
                        placeholder="Role name"
                      />
                      <input
                        type="number"
                        value={editPayRate.rate}
                        onChange={e => setEditPayRate(prev => ({ ...prev, rate: e.target.value }))}
                        className="form-input"
                        style={{ width: '80px', padding: '4px 8px', fontSize: '13px' }}
                        step="0.25"
                        min="0"
                        placeholder="Rate"
                      />
                      <button type="button" onClick={handleSavePayRateEdit}
                        style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>
                        Save
                      </button>
                      <button type="button" onClick={() => setEditingPayRateIdx(null)}
                        style={{ background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontWeight: 500, fontSize: '14px' }}>{pr.label}</span>
                      <span style={{ fontWeight: 600, color: '#1a2634', fontSize: '14px' }}>${pr.rate.toFixed(2)}/hr</span>
                      <button type="button" onClick={() => handleEditPayRate(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#6b7280' }}>
                        <Edit2 size={14} />
                      </button>
                      <button type="button" onClick={() => handleDeletePayRate(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#ef4444' }}>
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={newPayRate.label}
              onChange={e => setNewPayRate(prev => ({ ...prev, label: e.target.value }))}
              className="form-input"
              style={{ flex: 1, padding: '6px 10px', fontSize: '13px' }}
              placeholder="Role name (e.g., Cleaner, Driver)"
            />
            <input
              type="number"
              value={newPayRate.rate}
              onChange={e => setNewPayRate(prev => ({ ...prev, rate: e.target.value }))}
              className="form-input"
              style={{ width: '90px', padding: '6px 10px', fontSize: '13px' }}
              step="0.25"
              min="0"
              placeholder="$/hr"
            />
            <button type="button" onClick={handleAddPayRate}
              disabled={!newPayRate.label.trim() || !newPayRate.rate}
              style={{
                background: newPayRate.label.trim() && newPayRate.rate ? '#1a2634' : '#d1d5db',
                color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px',
                cursor: newPayRate.label.trim() && newPayRate.rate ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', whiteSpace: 'nowrap'
              }}>
              <Plus size={14} /> Add
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Hire Date</label>
          <input
            type="date"
            name="hireDate"
            className="form-input"
            value={formData.hireDate}
            onChange={handleChange}
          />
        </div>

        <div className="form-section-title">Weekly Availability</div>
        <p className="form-help-text" style={{ gridColumn: '1 / -1', margin: '-8px 0 16px 0' }}>
          Set when this employee is available to work. Scheduling outside these hours will show a warning.
        </p>

        <div className="availability-grid" style={{ gridColumn: '1 / -1' }}>
          {DAYS_OF_WEEK.map(({ key, label }) => (
            <div 
              key={key} 
              className={`availability-day ${!formData.availability[key].available ? 'unavailable' : ''}`}
            >
              <label className="day-toggle">
                <input
                  type="checkbox"
                  checked={formData.availability[key].available}
                  onChange={(e) => handleAvailabilityChange(key, 'available', e.target.checked)}
                />
                <span>{label}</span>
              </label>
              <div className="time-inputs">
                <input
                  type="time"
                  value={formData.availability[key].start}
                  onChange={(e) => handleAvailabilityChange(key, 'start', e.target.value)}
                  disabled={!formData.availability[key].available}
                />
                <span>to</span>
                <input
                  type="time"
                  value={formData.availability[key].end}
                  onChange={(e) => handleAvailabilityChange(key, 'end', e.target.value)}
                  disabled={!formData.availability[key].available}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="form-section-title">Default Shift</div>
        <p className="form-help-text" style={{ gridColumn: '1 / -1', margin: '-8px 0 16px 0' }}>
          Default shift times when creating a new week's schedule.
        </p>

        <div className="form-group">
          <label className="form-label">Start Time</label>
          <input
            type="time"
            className="form-input"
            value={formData.defaultShift.startTime}
            onChange={(e) => handleDefaultShiftChange('startTime', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">End Time</label>
          <input
            type="time"
            className="form-input"
            value={formData.defaultShift.endTime}
            onChange={(e) => handleDefaultShiftChange('endTime', e.target.value)}
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-save">
          {employee ? 'Save Changes' : 'Add Employee'}
        </button>
      </div>
    </form>
  );
}

export default EmployeeForm;
